import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { SISCard, SISCallout, SISInfluence, SISTakeaway, SISNoteImage } from "@/components/sis/SISLessonShell";
import { RomanNumeralAnalysis } from "@/components/song/RomanNumeralAnalysis";
import { YouTubeEmbed } from "@/components/song/YouTubeEmbed";
import { RagaLadder } from "@/components/RagaLadder";

export default function SouthIndian1() {
  const { lesson, course, accent } = findLesson("south-indian-songbook", "1");
  return (
    <LessonShell
      course={course}
      courseSlug="south-indian-songbook"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <YouTubeEmbed
        videoId="ey6szdICTSE"
        title="June Ponaal — Harris Jayaraj · Unnale Unnale (2007)"
        caption="Listen for the four-bar chord loop under the tune: Bm, F♯m with A in the bass, Em over G, then A and F♯7 back to Bm."
      />

      <SISCard label="The big idea" title="Why the bass line carries the loop">
        <p>
          June Ponaal is Harris Jayaraj at his most restrained — and that restraint is the lesson.
          The loop is only four bars long: <strong>Bm · F♯m/A · Em/G · A F♯7</strong>, then back to
          Bm. The inversions are chosen so the bass moves by step: B → A → G, then up to A. Play the
          F♯7 with A♯ in the bass, as Jason does, and the last bar steps A → A♯ → B straight into the
          next Bm. The chords are diatonic and familiar; what makes the loop feel smooth is that the
          bass walks instead of leaping.
        </p>
        <p>
          As Jason puts it when he lays out the chords: <em>"There's good voice leading."</em> Once you
          hear it, you'll notice how much of the song's ease comes from that one decision. The
          melody floats on top, but the bass is the road.
        </p>
      </SISCard>

      <RomanNumeralAnalysis
        label="Main progression · B minor (B Aeolian)"
        caption="Bm → F♯m/A → Em/G walks the bass down B–A–G. A and F♯7 share the last bar. The F♯7 uses a major dominant (V7) rather than the minor v — its A♯ creates a stronger pull back to Bm. Jason's chart also allows a plain Em in bar three; the bass player in the recording plays E there."
        chords={[
          { chord: "Bm", rn: "i", function: "Tonic", pitches: ["B3", "D4", "F#4", "B4"] },
          { bar: true },
          { chord: "F♯m/A", rn: "v/3", function: "Dominant", pitches: ["A3", "C#4", "F#4", "A4"] },
          { bar: true },
          { chord: "Em/G", rn: "iv/3", function: "Pre-dominant", pitches: ["G3", "B3", "E4", "G4"] },
          { bar: true },
          { chord: "A", rn: "VII", pitches: ["A3", "C#4", "E4", "A4"] },
          { chord: "F♯7", rn: "V7", function: "Dominant", pitches: ["F#3", "A#3", "C#4", "E4"] },
          { bar: true },
          { chord: "Bm", rn: "i", function: "Tonic", pitches: ["B3", "D4", "F#4", "B4"] },
        ]}
      />

      <SISCard label="The harmony in depth" title="Diatonic voice leading — what it means and why it matters">
        <p>
          <strong>Diatonic</strong> means every note and chord comes from one parent scale — in this
          case B Aeolian (the natural minor scale starting on B). The seven chords available are:
          Bm, C♯°, D, Em, F♯m, G, A. The loop uses four of them — Bm, F♯m, Em and A — plus the
          F♯7 at the end of the cycle. Nothing exotic. The magic is in the <em>inversions</em>.
        </p>
        <p>
          When Harris writes F♯m/A (F♯ minor with A in the bass), he's doing something specific: he
          wants A in the bass, not F♯. Why? Because the previous chord had B in the bass, and the
          next chord needs G. B → A → G is a descending stepwise line. Jason's warning: <em>"You
          might get mistaken for it to be A major. It's not an A major."</em> The A is only the bass —
          the chord above it is F♯ minor.
        </p>
        <p>
          The <strong>F♯7</strong> (major dominant seventh) is the one departure from pure Aeolian.
          In natural minor, the fifth chord is F♯m — a minor chord, weak on its own. By raising the
          third of that chord from A to A♯, Harris creates F♯7: a chord that has a tritone between
          A♯ and E, which screams for resolution back to B. This is the difference between a song
          that drifts and a song that arrives.
        </p>
      </SISCard>

      <SISCallout label="Practise this — the bass first, always">
        Play only the bass notes in your left hand, one bar each: B | A | G | A then A♯ (the A♯ is
        the bass of F♯7) — and back to B. Loop it for two minutes until it feels like breathing.
        Then — and only then — add the chord shapes in your right hand, keeping the bass notes
        exactly as they are.
        You are not playing chords. You are playing a moving bass line that happens to have
        harmonies resting on it.
      </SISCallout>

      <SISCard label="Carnatic context" title="B Aeolian = Natabhairavi (20th Melakarta)">
        <p>
          In the Carnatic system, the scale you're playing in B Aeolian has a name: <strong>Natabhairavi</strong>.
          It is the 20th Melakarta raga — one of the 72 parent scales. In solfège syllables rooted
          on Sa = B: Sa–Ri₂–Ga₂–Ma₁–Pa–Dha₁–Ni₂. Notice that Dha₁ (G) is the flat sixth and
          Ni₂ (A) is the flat seventh. These are the characteristic notes of the Aeolian mode.
        </p>
        <p>
          The one note outside it is the A♯ in F♯7. Raising Ni₂ (A) to Ni₃ (A♯) turns Natabhairavi
          into <strong>Kiravani</strong>, the 21st Melakarta — the Carnatic name for the harmonic
          minor. Jason points out that you can write the tune either way: in swaras or in actual
          B minor note names.
        </p>
      </SISCard>

      <RagaLadder
        title="Natabhairavi — Sa = B (B Aeolian)"
        tonic="B"
        steps={[
          { swara: "Sa",  pitch: "B3",  notes: "Tonic — home",           flat: false },
          { swara: "Ri₂", pitch: "C#4", notes: "Major 2nd",              flat: false },
          { swara: "Ga₂", pitch: "D4",  notes: "Minor 3rd — the sadness", flat: true  },
          { swara: "Ma₁", pitch: "E4",  notes: "Perfect 4th",            flat: false },
          { swara: "Pa",  pitch: "F#4", notes: "Perfect 5th",            flat: false },
          { swara: "Dha₁",pitch: "G4",  notes: "Minor 6th — the longing", flat: true  },
          { swara: "Ni₂", pitch: "A4",  notes: "Minor 7th",              flat: true  },
          { swara: "Sa",  pitch: "B4",  notes: "Octave — resolution",    flat: false },
        ]}
      />

      <SISInfluence artists={["A.R. Rahman", "Bill Evans (piano voicings)", "Bach chorales"]}>
        <p>
          Before his 2001 debut with <em>Minnale</em>, Harris Jayaraj worked as an additional
          programmer under A.R. Rahman, among other composers. For the voice-leading idea itself,
          listen to Bach's chorales — four voices that each move as little as possible from chord
          to chord — and to Bill Evans, whose piano voicings are a jazz-era study in the same
          habit.
        </p>
      </SISInfluence>

      <SISCard label="Right-hand voicings" title="How to voice the chords so the bass sings">
        <p>
          The trick is <strong>close-position voicings</strong> in the right hand, leaving the bass
          note for the left hand alone. For Bm: right hand plays D–F♯–B (close position, third at
          the bottom). For F♯m/A: left hand holds A in octaves, right hand plays C♯–F♯–A. For Em/G:
          left hand holds G in octaves, right hand plays G–B–E.
        </p>
        <p>
          For F♯7, Jason plays it as an inversion with A♯ (B♭) in the bass, so the bass steps
          A → A♯ → B into the next Bm — the A♯ becomes a passing note. Once the shapes are secure,
          try spreading them out. In his words: <em>"Another nice thing to make these sorts of
          sounds sound better would be spread voicing."</em>
        </p>
      </SISCard>

      <SISNoteImage
        src="/notes/south-indian-songbook/finals/01-june-ponaal-page1.png"
        caption="Jason's chart — the seven chords of B minor (with F♯7 pointing home to Bm), the loop Bm · F♯m/A · Em/G (or Em) · A F♯7, and the rhythm of the tune with its opening notes D C♯ B A E F♯."
      />

      <SISTakeaway>
        Voice leading is not an advanced technique — it is the <em>foundation</em> of melody writing.
        Every time you write or play a chord progression, ask yourself: what does my bass do? Does
        it leap or does it walk? If it leaps, there will be a jolt. If it walks, the listener will
        feel at home. June Ponaal teaches you to walk before you run.
      </SISTakeaway>
    </LessonShell>
  );
}
